import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Repeat, Ban } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const STATUS = { open: "In asta", awarded: "Aggiudicato", paid: "Pagato", reassigning: "In riassegnazione" };
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const MIN_REASON = 5;

function Reassignment({ o, onPropose, busy }) {
  const w = o.withdrawal || {};
  return (
    <div className="mt-1 rounded-lg bg-orange-50 border border-orange-200 p-2 text-xs text-slate-700" data-testid={`admin-reassignment-${o.id}`}>
      <div><b>{w.supplier_name}</b> ha rinunciato{w.at ? ` il ${new Date(w.at).toLocaleString("it-IT")}` : ""}: "{w.reason}"</div>
      {o.proposal && <div className="mt-1 text-orange-800">Proposto a <b>{o.proposal.supplier_name}</b> a {eur(o.proposal.price)} · in attesa di risposta</div>}
      {o.next_candidate && (
        <button onClick={() => onPropose(o)} disabled={busy} className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-semibold disabled:opacity-50" data-testid={`admin-propose-${o.id}`}>
          <Repeat className="w-3 h-3" /> {o.proposal ? "Ritira e proponi a" : "Proponi a"} {o.next_candidate.supplier_name} ({eur(o.next_candidate.price)})
        </button>
      )}
      {!o.proposal && !o.next_candidate && <div className="mt-1 font-semibold text-red-700">Nessun'altra offerta disponibile: annulla l'ordine.</div>}
    </div>
  );
}

export default function AdminOrdersTable() {
  const [filter, setFilter] = useState("");
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get(`/admin/orders${filter ? `?status=${filter}` : ""}`).then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { setList(null); load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const propose = async (o) => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/orders/${o.id}/propose`, { skip_pending: !!o.proposal }); toast.success(`Ordine proposto a ${eur(data.price)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${cancelling.id}/cancel`, { reason: reason.trim() });
      toast.success("Ordine annullato · le parti sono state avvisate");
      setCancelling(null); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reassigning = (list || []).filter(o => o.status === "reassigning").length;

  return (
    <div className="ga-card p-5" data-testid="admin-orders-table">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Ordini non consegnati {list && `(${list.length}${reassigning ? ` · ${reassigning} da riassegnare` : ""})`}</div>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="text-sm rounded-full border border-slate-300 px-3 py-1.5" data-testid="admin-orders-filter">
          <option value="">Tutti</option><option value="reassigning">Da riassegnare</option><option value="open">In asta</option><option value="awarded">Aggiudicati, da pagare</option><option value="paid">Pagati, da consegnare</option>
        </select>
      </div>
      <div className="mt-1 text-xs text-slate-500">Se il fornitore vincitore rinuncia, proponi l'ordine alla prossima offerta migliore: il fornitore può accettare al prezzo che aveva offerto. Se nessuno accetta, annulla l'ordine.</div>
      {list === null && <div className="mt-3 text-sm text-slate-500">Caricamento...</div>}
      {list?.length === 0 && <div className="mt-3 text-sm text-slate-500" data-testid="admin-orders-empty">Nessun ordine.</div>}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3">Ordine</th><th className="pr-3">Acquirente → Fornitore</th><th className="pr-3">Stato</th><th className="pr-3">Importo</th><th className="pr-3">Creato</th><th className="pr-3"></th>
          </tr></thead>
          <tbody>
            {(list || []).map(o => (
              <tr key={o.id} className="border-b border-slate-100 align-top" data-testid={`admin-order-${o.id}`}>
                <td className="py-2 pr-3"><div className="font-semibold text-slate-900">{o.title}</div><div className="text-[10px] font-mono text-slate-400">{o.bids_count} offerte</div>{o.status === "reassigning" && <Reassignment o={o} onPropose={propose} busy={busy} />}</td>
                <td className="pr-3 text-xs">{o.restaurant_name} → {o.winner_name || "—"}</td>
                <td className="pr-3 text-xs">{STATUS[o.status] || o.status}</td>
                <td className="pr-3 text-xs font-mono whitespace-nowrap">{o.winning_price != null ? eur(o.winning_price) : "—"}</td>
                <td className="pr-3 text-xs whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleString("it-IT") : "—"}</td>
                <td className="pr-3 text-xs">
                  <button onClick={() => { setReason(""); setCancelling(o); }} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 px-3 py-1 text-xs font-semibold hover:bg-red-50" data-testid={`admin-cancel-${o.id}`}><Ban className="w-3 h-3" /> Annulla</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cancelling} onOpenChange={(open) => { if (!open) setCancelling(null); }}>
        <DialogContent data-testid="admin-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Annullare "{cancelling?.title}"?</DialogTitle>
            <DialogDescription>
              Ristorante e fornitori coinvolti ricevono un avviso con il motivo.{cancelling?.paid ? " Il pagamento viene annullato o rimborsato." : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="admin-cancel-reason">Motivo <span className="text-slate-400 text-xs">(obbligatorio)</span></Label>
            <Textarea id="admin-cancel-reason" rows={3} maxLength={300} value={reason} onChange={e => setReason(e.target.value)} placeholder="Per esempio: richiesta del ristorante, nessun fornitore disponibile" data-testid="admin-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCancelling(null)}>Indietro</Button>
            <Button onClick={cancel} disabled={busy || reason.trim().length < MIN_REASON} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="admin-cancel-confirm">Annulla ordine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
